import React, { useMemo, useEffect, useCallback, useContext, useState } from 'react'
import useInterval from 'hooks/useInterval'
import usePrevious from 'hooks/usePrevious'
import { getData as apiGetData } from 'api/Api'
import { useIdRegister } from 'hooks/useIdRegister'
import * as UserPresenceStatuses from 'const/UserPresenceStatuses'
import moment from 'moment'
import { useSelector } from 'react-redux'

const DEFAULT_CONTEXT: {
  userPresences: Map<number, number>;
  registerUserPresence: (userId: number) => void;
  unregisterUserPresence: (userId: number) => void;
} = {
  userPresences: new Map(),
  registerUserPresence: () => {},
  unregisterUserPresence: () => {},
}

export const UserPresenceProviderContext = React.createContext(DEFAULT_CONTEXT)

const INTERVAL = 20000

export function UserPresenceProvider ({
  children
}) {
  const [lastSeenTimestamps, setLastSeenTimestamps] = useState<Map<number, number>>(() => new Map())
  const [ids, registerId, unregisterId] = useIdRegister<number>(0)
  const prevIds = usePrevious(ids)

  const fetchUserLastSeenTimestamps = useCallback((userIds = []) => {
    if (!userIds.length) return

    try {
      const getDataPromise = apiGetData('UsersLastSeen', { ids: userIds })

      Promise.race([
        getDataPromise,
        new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error())
          }, 30000)
        })
      ]).then(({ Users }) => {
        Users.forEach(User =>
          setLastSeenTimestamps(lastSeenTimestamps =>
            new Map(lastSeenTimestamps).set(User.Id, User.Timestamp)
          )
        )
      }).catch(() => {
        getDataPromise.cancel()
      })
    } catch (e) {} // eslint-disable-line no-empty
  }, [])

  useEffect(() => {
    let addedIds: number[] = []

    if (ids.length) {
      if (prevIds?.length) {
        addedIds = ids.filter(boardId => !prevIds.includes(boardId))
      } else {
        addedIds = ids
      }
    }

    if (addedIds.length) {
      fetchUserLastSeenTimestamps(addedIds)
    }
  }, [ids])

  useInterval(() => {
    fetchUserLastSeenTimestamps(ids)
  }, ids.length ? INTERVAL : null)

  const userPresences = useMemo(() => {
    const userPresences = new Map<number, number>()

    Array.from(lastSeenTimestamps).forEach(([userId, lastSeenTimestamp]) => {
      let status = UserPresenceStatuses.OFFLINE

      if (
        lastSeenTimestamp &&
        moment().diff(lastSeenTimestamp, 'seconds') <= 60
      ) {
        status = UserPresenceStatuses.ONLINE
      }

      userPresences.set(userId, status)
    })

    return userPresences
  }, [lastSeenTimestamps])

  const value = useMemo(() => ({
    userPresences,
    registerUserPresence: registerId,
    unregisterUserPresence: unregisterId,
  }), [userPresences, registerId, unregisterId])

  return (
    <UserPresenceProviderContext.Provider value={value}>
      {children}
    </UserPresenceProviderContext.Provider>
  )
}

export function useUserPresenceProvider (userId: number) {
  const { userPresences, registerUserPresence, unregisterUserPresence } = useContext(UserPresenceProviderContext)

  const userPresenceEnabled = useSelector<any, boolean>(state => state.kanbo.config.userPresence)

  useEffect(() => {
    if (userPresenceEnabled) {
      registerUserPresence(userId)

      return () => {
        unregisterUserPresence(userId)
      }
    }
  }, [userPresenceEnabled, userId])

  return userPresences.get(userId)
}